import { Component } from '@theme/component';
import {
  fetchConfig,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} cartStatus - The polite cart update status.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #quantityTimers = new Map();
  #queuedQuantityChanges = new Map();
  #mutationQueue = Promise.resolve();
  #pendingMutations = 0;

  #queueQuantityChange = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const row = event.target.closest('[data-key]');
    const key = row?.dataset.key || '';
    const timerKey = key || String(line);
    const active = document.activeElement;
    const focusName = active instanceof HTMLButtonElement ? active.name : '';
    const existing = this.#quantityTimers.get(timerKey);
    if (existing) window.clearTimeout(existing);

    this.#queuedQuantityChanges.set(timerKey, { quantity, line, key, focusName });

    this.#quantityTimers.set(timerKey, window.setTimeout(() => {
      this.#quantityTimers.delete(timerKey);
      const change = this.#queuedQuantityChanges.get(timerKey);
      this.#queuedQuantityChanges.delete(timerKey);
      if (change) this.#onQuantityChange(change);
    }, 300));
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    this.#quantityTimers.forEach((timer) => window.clearTimeout(timer));
    this.#quantityTimers.clear();
    this.#queuedQuantityChanges.clear();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {{quantity: number, line: number, key: string, focusName: string}} change - The requested change.
   */
  #onQuantityChange(change) {
    const { quantity, line, key, focusName } = change;

    if (quantity === 0) {
      return this.onLineItemRemove(line, key, focusName);
    }

    const operation = this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
      focusName,
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return operation;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
    return operation;
  }

  /** Flushes debounced changes and resolves after every queued cart mutation. */
  whenIdle() {
    const pending = [];
    for (const [timerKey, timer] of this.#quantityTimers.entries()) {
      window.clearTimeout(timer);
      this.#quantityTimers.delete(timerKey);
      const change = this.#queuedQuantityChanges.get(timerKey);
      this.#queuedQuantityChanges.delete(timerKey);
      if (change) pending.push(this.#onQuantityChange(change));
    }
    return Promise.all(pending).then(() => this.#mutationQueue.catch(() => {}));
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line, keyOrEvent = '', focusName = '') {
    const cartItemRow = this.refs.cartItemRows[line - 1];
    // Declarative `on:click` handlers pass the click event after route data.
    // Never serialize that Event as Shopify's cart line id.
    const key = typeof keyOrEvent === 'string' ? keyOrEvent : '';
    const lineKey = key || cartItemRow?.dataset.key || '';
    if (!cartItemRow || !window.AvoraCartInteractions) return;
    const approved = await window.AvoraCartInteractions.confirmRemoval(
      lineKey, cartItemRow.querySelector('.cart-items__title')?.textContent?.trim()
    );
    if (!approved || !this.querySelector(`[data-key="${CSS.escape(lineKey)}"]`)) return;
    const operation = this.updateQuantity({
      line,
      key: lineKey,
      quantity: 0,
      action: 'clear',
      focusName,
    });

    cartItemRow?.classList.add('is-updating');
    return operation;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} config.key - The stable cart line key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} config.focusName - The quantity control to restore.
   */
  updateQuantity(config) {
    this.#pendingMutations += 1;
    this.#disableCartItems();

    const operation = this.#mutationQueue
      .catch(() => {})
      .then(() => this.#performQuantityUpdate(config));

    this.#mutationQueue = operation;
    return operation.finally(() => {
      this.#pendingMutations -= 1;
      if (this.#pendingMutations === 0) this.#enableCartItems();
    });
  }

  /**
   * Performs one serialized cart mutation.
   * @param {{line: number, key: string, quantity: number, action: string, focusName: string}} config
   */
  #performQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const request = /** @type {Record<string, unknown>} */ ({
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    if (key) request.id = key;
    else request.line = line;
    const body = JSON.stringify(request);

    cartTotal?.shimmer();
    const controller = new AbortController();
    const requestTimer = window.setTimeout(() => controller.abort(), 12000);

    return fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig('json', { body }), signal: controller.signal })
      .then((response) => {
        return response.text().then((responseText) => ({ ok: response.ok, responseText }));
      })
      .then(({ ok, responseText }) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (!ok || parsedResponseText.errors) {
          const message = parsedResponseText.description || parsedResponseText.errors || 'Could not update your cart.';
          throw new Error(typeof message === 'string' ? message : 'Could not update your cart.');
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        this.#announce(`Cart updated. ${newCartItemCount} item${newCartItemCount === 1 ? '' : 's'}.`);

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
        this.#restoreMutationFocus(config);
        return parsedResponseText;
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
        const message = error instanceof Error && error.name !== 'AbortError' ? error.message : 'Could not update your cart. Please try again.';
        this.#handleCartError(line, { errors: message });
        this.#announce(message);
        // A failed recovery read must not reject the event handler or leave
        // checkout waiting forever. Keep the inline error and reset controls.
        return sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});
      })
      .finally(() => {
        window.clearTimeout(requestTimer);
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.refs.cartStatus;
    if (!(status instanceof HTMLElement)) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  }

  /** @param {{key: string, focusName: string}} config */
  #restoreMutationFocus(config) {
    requestAnimationFrame(() => {
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || document;
      const escapedKey = window.CSS?.escape ? window.CSS.escape(config.key) : config.key.replace(/(["\\])/g, '\\$1');
      const row = config.key ? section.querySelector(`[data-key="${escapedKey}"]`) : null;
      let target = config.focusName ? row?.querySelector(`[name="${config.focusName}"]`) : null;
      if (!(target instanceof HTMLElement)) {
        target = section.querySelector('cart-items-component button:not([disabled]), cart-items-component a[href]');
      }
      target?.focus();
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event.detail.renderedSectionId === this.sectionId) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    // Custom cart integrations may emit the shared event without pre-rendered
    // sections. Refresh this section instead of throwing on a partial payload.
    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
